import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LUNAR } from '../constants/testIds';
import { Ship, LayoutDashboard, LogOut, Menu, X, Shield } from 'lucide-react';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate('/');
  };

  const navLinks = [
    { label: 'Home', to: '/', testId: 'nav-home' },
    { label: 'Auction Preview', to: '/upcoming', testId: LUNAR.navUpcoming },
    { label: 'Previous Auction Results', to: '/past', testId: LUNAR.navPast },
  ];

  return (
    <nav className="sticky top-0 z-50 glassmorphism border-b border-white/10 px-5 py-3.5 md:px-12 md:py-4 flex items-center justify-between">
      {/* Logo */}
      <Link to="/" className="flex items-center gap-3 group" data-testid={LUNAR.navLogo} onClick={() => setMobileOpen(false)}>
        <div className="w-9 h-9 border border-luxury-gold/70 flex items-center justify-center bg-black group-hover:border-luxury-gold transition-colors duration-300">
          <span className="text-luxury-gold font-serif-luxury font-semibold text-lg leading-none">L</span>
        </div>
        <div className="flex flex-col justify-center">
          <span className="font-serif-luxury text-base md:text-lg tracking-[0.28em] text-white leading-none">LUNAR&nbsp;LEGACIES</span>
          <span className="hidden sm:block text-[8px] font-sans-luxury tracking-[0.42em] text-luxury-gold/80 leading-none mt-1.5">EST. 1998 · SPACE MEMORABILIA</span>
        </div>
      </Link>

      {/* Desktop Nav Links */}
      <div className="hidden md:flex items-center space-x-7 text-[14px] font-sans-luxury tracking-[0.16em] uppercase text-luxury-muted">
        {navLinks.map((link) => (
          <Link key={link.label} to={link.to} className="hover:text-luxury-gold transition-colors duration-300" data-testid={link.testId}>
            {link.label}
          </Link>
        ))}
        <Link to="/consign" className="text-luxury-goldLight hover:text-luxury-gold transition-colors duration-300 flex items-center gap-1.5" data-testid={LUNAR.navConsign}>
          <Ship size={15} className="stroke-[1.5]" />
          Consign
        </Link>
        <Link to="/#mailing-list" className="text-luxury-gold hover:text-black hover:bg-luxury-gold border border-luxury-gold/50 px-3.5 py-2 transition-all duration-300" data-testid="nav-email-list">
          Join Our Email List
        </Link>
      </div>

      {/* Desktop CTA (admin-only once logged in; hidden for public visitors) */}
      <div className="hidden md:flex items-center gap-4">
        {user && (
          <>
            {user.role === 'admin' && (
              <Link to="/admin" className="text-black bg-luxury-gold hover:bg-luxury-goldLight font-sans-luxury text-[11px] tracking-[0.18em] uppercase font-semibold flex items-center gap-1.5 py-2 px-3.5 transition-all duration-300" data-testid="nav-admin-link">
                <Shield size={13} />
                Admin
              </Link>
            )}
            <Link to="/dashboard" className="text-white hover:text-luxury-gold font-sans-luxury text-[11px] tracking-[0.18em] uppercase flex items-center gap-1.5 py-2 px-3.5 border border-white/10 bg-white/5 hover:border-luxury-gold/50 transition-all duration-300" data-testid={LUNAR.navDashboard}>
              <LayoutDashboard size={13} className="text-luxury-gold" />
              Dashboard
            </Link>
            <button onClick={handleLogout} className="text-luxury-muted hover:text-red-400 font-sans-luxury text-[11px] tracking-[0.18em] uppercase flex items-center gap-1.5 py-2 px-2 transition-colors duration-300" data-testid={LUNAR.navLogoutButton}>
              <LogOut size={13} />
              Sign Out
            </button>
          </>
        )}
      </div>

      {/* Mobile hamburger */}
      <button
        className="md:hidden text-luxury-gold p-1.5 -mr-1.5"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label="Toggle menu"
        data-testid="nav-mobile-toggle"
      >
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 glassmorphism border-b border-white/10 flex flex-col px-6 py-6 space-y-5" data-testid="nav-mobile-menu">
          {navLinks.map((link) => (
            <Link key={link.label} to={link.to} onClick={() => setMobileOpen(false)} className="text-luxury-muted hover:text-luxury-gold font-sans-luxury text-sm tracking-[0.2em] uppercase transition-colors duration-300" data-testid={`${link.testId}-mobile`}>
              {link.label}
            </Link>
          ))}
          <Link to="/consign" onClick={() => setMobileOpen(false)} className="text-luxury-goldLight hover:text-luxury-gold font-sans-luxury text-sm tracking-[0.2em] uppercase flex items-center gap-2 transition-colors duration-300">
            <Ship size={15} /> Consign
          </Link>
          {user && (
            <div className="pt-4 border-t border-white/10">
              <div className="flex flex-col gap-4">
                {user.role === 'admin' && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-luxury-gold hover:text-luxury-goldLight font-sans-luxury text-sm tracking-[0.2em] uppercase flex items-center gap-2 font-semibold" data-testid="nav-admin-link-mobile">
                    <Shield size={15} /> Admin
                  </Link>
                )}
                <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="text-white hover:text-luxury-gold font-sans-luxury text-sm tracking-[0.2em] uppercase flex items-center gap-2" data-testid={`${LUNAR.navDashboard}-mobile`}>
                  <LayoutDashboard size={15} className="text-luxury-gold" /> Dashboard
                </Link>
                <button onClick={handleLogout} className="text-left text-luxury-muted hover:text-red-400 font-sans-luxury text-sm tracking-[0.2em] uppercase flex items-center gap-2" data-testid={`${LUNAR.navLogoutButton}-mobile`}>
                  <LogOut size={15} /> Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};
